// src/components/ProjectWebsitePlanstand.jsx
// Planstand-Register im Reiter "Website" (US-WEBP-002): Tabelle mit Plannummer,
// Titel, Index, Datum und PDF (Platzhalter). Jeder Indexstand hat einen eigenen,
// unveränderlichen Link auf die externe Seite — als Text und als QR-Code-PNG
// (US-WEBP-003, siehe PlanstandQrDialog).
//
// Kernregel: ein neuer Index erzeugt eine NEUE Version mit NEUEM Link. Der alte
// Link bleibt gültig, gilt aber als veraltet und zeigt extern auf den neuen Stand.
// Indexstände sind darum unveränderlich — korrigiert wird über Löschen des Plans.
// Einzige Ausnahme: das Datum eines Index lässt sich über „Bearbeiten" nachträglich
// korrigieren (rein kosmetisch, siehe ADR 0046 Nachtrag) — Link und Index bleiben
// dabei unangetastet.
//
// Reihenfolge und Gruppen (US-WEBP-005): das Register lässt sich sortieren und in
// benannte Abschnitte gliedern. Beides ist reine Darstellung und rührt an KEINEM
// Link — Gruppe und Reihenfolge hängen am Plan, nicht am Indexstand.
//
// Alle Operationen schreiben SOFORT (nicht über den "Speichern"-Button des Reiters),
// weil der erzeugte Link unmittelbar kopiert und verteilt wird.
import React, { useMemo, useState } from "react";
import {
  buildPlanstandDateiname,
  buildPlanstandUrl,
} from "../services/azureProjectWebsiteService";
import PlanstandQrDialog from "./PlanstandQrDialog";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const emptyPlanForm = () => ({
  nummer: "",
  titel: "",
  quelle: "",
  gruppe: "",
  index: "",
  datum: "",
});

// Anzahl Spalten der Tabelle — die Formular-, Warn- und Verlaufszeilen spannen
// sich darüber. Als Konstante, damit eine neue Spalte nicht an fünf Stellen
// nachgezogen werden muss.
const SPALTEN = 9;

// Bucket-Schlüssel und Beschriftung der Pläne ohne Gruppe. Der Schlüssel ist ""
// (genau das, was in Azure steht), die Beschriftung nur Anzeige.
const OHNE_GRUPPE = "Ohne Gruppe";

// Pläne in Abschnitte gliedern. Die Reihenfolge der Gruppen ergibt sich aus dem
// ersten Auftreten in der nach SortOrder sortierten Liste, innerhalb einer Gruppe
// gilt weiterhin SortOrder. Damit ist die Darstellung auch dann eindeutig, wenn
// die Pläne einer Gruppe (noch) nicht zusammenhängend einsortiert sind — etwa
// direkt nachdem jemand über "Bearbeiten" eine Gruppe gesetzt hat. Pläne ohne
// Gruppe stehen immer zuletzt.
const gruppieren = (plaene) => {
  const ohne = [];
  const nachName = new Map();
  for (const plan of plaene) {
    const key = (plan.gruppe || "").trim();
    if (!key) {
      ohne.push(plan);
      continue;
    }
    if (!nachName.has(key)) nachName.set(key, []);
    nachName.get(key).push(plan);
  }
  const gruppen = [...nachName.entries()].map(([key, liste]) => ({
    key,
    titel: key,
    plaene: liste,
  }));
  if (ohne.length > 0) gruppen.push({ key: "", titel: OHNE_GRUPPE, plaene: ohne });
  return gruppen;
};

// Link mit Kopieren- und QR-Knopf. Der Link ist das Arbeitsmittel für die
// Baustelle (Aushang/QR), darum bewusst vollständig sichtbar und selektierbar.
const LinkZelle = ({ url, veraltet, onQr }) => {
  const [kopiert, setKopiert] = useState(false);

  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 1500);
    } catch {
      // Clipboard-API kann blockiert sein — dann bleibt der Text zum Markieren.
      setKopiert(false);
    }
  };

  return (
    <div className="planstand__link">
      <code className={veraltet ? "planstand__link-url is-veraltet" : "planstand__link-url"}>
        {url}
      </code>
      <button
        type="button"
        className="planstand__link-copy"
        onClick={kopieren}
        title="Link kopieren"
      >
        {kopiert ? "kopiert" : "kopieren"}
      </button>
      <button
        type="button"
        className="planstand__link-qr"
        onClick={onQr}
        title="QR-Code anzeigen und als PNG herunterladen"
      >
        QR-Code
      </button>
    </div>
  );
};

const ProjectWebsitePlanstand = ({
  plaene,
  projectCode,
  disabled,
  onCreate,
  onAddIndex,
  onUpdateMeta,
  onSetCurrent,
  onDelete,
  onReorder,
}) => {
  const [neuForm, setNeuForm] = useState(emptyPlanForm());
  const [neuOffen, setNeuOffen] = useState(false);
  const [indexFor, setIndexFor] = useState(null); // planId
  const [indexForm, setIndexForm] = useState({ index: "", datum: "" });
  const [metaFor, setMetaFor] = useState(null); // planId
  const [metaForm, setMetaForm] = useState({
    nummer: "",
    titel: "",
    quelle: "",
    gruppe: "",
    datum: "",
    versionId: "",
  });
  const [historieFor, setHistorieFor] = useState(null); // planId
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [qrFor, setQrFor] = useState(null); // { url, dateiname, kopfzeile, veraltet }
  const [griffFor, setGriffFor] = useState(null); // planId, dessen Griff gepackt ist
  const [ziehtId, setZiehtId] = useState(null); // planId, der gerade gezogen wird
  const [ziel, setZiel] = useState(null); // { id, position: "vor"|"nach" } | { gruppe }
  const [umbenennenFor, setUmbenennenFor] = useState(null); // Gruppenname
  const [gruppenName, setGruppenName] = useState("");

  const gruppen = useMemo(() => gruppieren(plaene), [plaene]);
  const hatGruppen = gruppen.some((g) => g.key !== "");
  // Vorschlagsliste, damit eine bestehende Gruppe nicht durch einen Tippfehler
  // zur zweiten wird — Gruppen sind Text, es gibt keine Kennung dahinter.
  const gruppenNamen = gruppen.filter((g) => g.key !== "").map((g) => g.key);

  const lauf = async (fn) => {
    try {
      setBusy(true);
      setFehler(null);
      await fn();
      return true;
    } catch (err) {
      setFehler(err?.message || "Aktion fehlgeschlagen.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  // --- Reihenfolge und Gruppen ----------------------------------------------
  // Alles, was an der Anordnung dreht, läuft über dieselbe flache Liste
  // [{ id, gruppe }] in Anzeigereihenfolge. Der Service leitet daraus SortOrder
  // und Gruppe je Plan ab — es gibt damit nur einen Schreibweg und keine zweite
  // Stelle, an der Anzeige und gespeicherte Reihenfolge auseinanderlaufen können.
  const flacheListe = () =>
    gruppen.flatMap((g) => g.plaene.map((p) => ({ id: p.id, gruppe: g.key })));

  const ordnungSetzen = (liste) => lauf(() => onReorder(liste));

  // Einen Plan an eine Zielposition setzen. Die Gruppe des Ziels wird übernommen —
  // ein Plan, den man in einen anderen Abschnitt zieht, gehört danach dorthin.
  const planEinfuegen = async (planId, zielId, position, zielGruppe) => {
    const liste = flacheListe().filter((e) => e.id !== planId);
    const eintrag = { id: planId, gruppe: zielGruppe };
    const idx = zielId ? liste.findIndex((e) => e.id === zielId) : -1;
    if (idx === -1) liste.push(eintrag);
    else liste.splice(position === "nach" ? idx + 1 : idx, 0, eintrag);
    await ordnungSetzen(liste);
  };

  // Pfeiltasten verschieben bewusst NUR innerhalb der Gruppe und sind am Rand
  // deaktiviert: ein Klick soll nie unbemerkt die Gruppenzuteilung ändern.
  // Der Wechsel in eine andere Gruppe geht über Ziehen oder über "Bearbeiten".
  const planVerschieben = async (gruppe, position, richtung) => {
    const nachbar = position + richtung;
    if (nachbar < 0 || nachbar >= gruppe.plaene.length) return;
    const neu = [...gruppe.plaene];
    [neu[position], neu[nachbar]] = [neu[nachbar], neu[position]];
    const liste = gruppen.flatMap((g) =>
      (g.key === gruppe.key ? neu : g.plaene).map((p) => ({ id: p.id, gruppe: g.key }))
    );
    await ordnungSetzen(liste);
  };

  // Eine ganze Gruppe mit allen ihren Plänen verschieben. "Ohne Gruppe" bleibt
  // dabei aussen vor und immer zuletzt — sonst wäre die Restmenge sortierbar wie
  // ein Abschnitt, ohne einer zu sein.
  const gruppeVerschieben = async (key, richtung) => {
    const echte = gruppen.filter((g) => g.key !== "");
    const pos = echte.findIndex((g) => g.key === key);
    const nachbar = pos + richtung;
    if (pos === -1 || nachbar < 0 || nachbar >= echte.length) return;
    [echte[pos], echte[nachbar]] = [echte[nachbar], echte[pos]];
    const rest = gruppen.filter((g) => g.key === "");
    const liste = [...echte, ...rest].flatMap((g) =>
      g.plaene.map((p) => ({ id: p.id, gruppe: g.key }))
    );
    await ordnungSetzen(liste);
  };

  // Umbenennen heisst: allen Plänen dieser Gruppe den neuen Namen geben. Ein
  // bereits bestehender Name führt die beiden Abschnitte zusammen — das ist der
  // gewollte Weg, zwei versehentlich getrennte Gruppen zu vereinen.
  const gruppeUmbenennen = async (e, key) => {
    e.preventDefault();
    const neu = gruppenName.trim();
    if (!neu || neu === key) {
      setUmbenennenFor(null);
      return;
    }
    const liste = gruppen.flatMap((g) =>
      g.plaene.map((p) => ({ id: p.id, gruppe: g.key === key ? neu : g.key }))
    );
    const ok = await ordnungSetzen(liste);
    if (ok) setUmbenennenFor(null);
  };

  const zielIst = (id, position) =>
    ziel && ziel.id === id && ziel.position === position;

  // Ober- oder untere Hälfte der Zeile entscheidet, ob vor oder nach ihr
  // eingefügt wird — sonst wäre die letzte Position eines Abschnitts nicht
  // erreichbar.
  const ueberZeile = (e, planId) => {
    if (!ziehtId || ziehtId === planId) return;
    e.preventDefault();
    const box = e.currentTarget.getBoundingClientRect();
    const position = e.clientY - box.top > box.height / 2 ? "nach" : "vor";
    setZiel((p) =>
      p && p.id === planId && p.position === position ? p : { id: planId, position }
    );
  };

  const abwurfBeenden = () => {
    setGriffFor(null);
    setZiehtId(null);
    setZiel(null);
  };

  // Auf der Zeile abgelegt: an die markierte Stelle einfügen und die Gruppe des
  // Ziels übernehmen.
  const aufZeileAblegen = async (e, planId, gruppenKey) => {
    e.preventDefault();
    const position = ziel && ziel.id === planId ? ziel.position : "vor";
    const gezogen = ziehtId;
    abwurfBeenden();
    if (gezogen && gezogen !== planId) {
      await planEinfuegen(gezogen, planId, position, gruppenKey);
    }
  };

  // Der QR-Dialog hängt immer an genau einem Indexstand: Link und Dateiname
  // gehören zum Index, nicht zum Plan. Sonst liesse sich ein ausgedruckter Code
  // nicht mehr dem Stand zuordnen, für den er erzeugt wurde.
  const qrOeffnen = (plan, version) =>
    setQrFor({
      url: buildPlanstandUrl(projectCode, version.linkId),
      dateiname: buildPlanstandDateiname(plan, version),
      kopfzeile: `${plan.nummer} ${plan.titel} – Index ${version.index || "–"}`,
      veraltet: !!version.veraltet,
    });

  const planAnlegen = async (e) => {
    e.preventDefault();
    const ok = await lauf(() => onCreate(neuForm));
    if (ok) {
      setNeuForm(emptyPlanForm());
      setNeuOffen(false);
    }
  };

  const indexErfassen = async (e, planId) => {
    e.preventDefault();
    const ok = await lauf(() => onAddIndex(planId, indexForm));
    if (ok) {
      setIndexForm({ index: "", datum: "" });
      setIndexFor(null);
      setHistorieFor(planId); // neuen Stand und den nun veralteten Link zeigen
    }
  };

  const metaSpeichern = async (e, planId) => {
    e.preventDefault();
    const ok = await lauf(() => onUpdateMeta(planId, metaForm));
    if (ok) setMetaFor(null);
  };

  const standAktivieren = async (plan, version) => {
    if (
      !window.confirm(
        `Index ${version.index || "–"} als aktuellen Stand von ${plan.nummer} setzen? ` +
          "Dessen Link gilt danach als gültig, die übrigen als veraltet."
      )
    ) {
      return;
    }
    await lauf(() => onSetCurrent(plan.id, version.id));
  };

  const planLoeschen = async (plan) => {
    if (
      !window.confirm(
        `Plan ${plan.nummer} "${plan.titel}" mit allen ${plan.versionen.length} Indexständen löschen? ` +
          "Alle bisher verteilten Links dieses Plans werden damit ungültig."
      )
    ) {
      return;
    }
    await lauf(() => onDelete(plan.id));
  };

  return (
    <div className="planstand">
      <div className="planstand__intro">
        <strong>Planstand-Register.</strong> Je Plan entsteht pro Index ein eigener,
        dauerhafter Link für die Baustelle. Ein neuer Index erzeugt einen neuen Link;
        der alte bleibt erreichbar und weist extern darauf hin, dass er veraltet ist.
        Zu jedem Stand lässt sich der QR-Code anzeigen und als PNG herunterladen.
        {!disabled && (
          <>
            {" "}
            Die Reihenfolge lässt sich am Griff links ziehen oder mit den Pfeilen
            ändern; über „Bearbeiten" bekommt ein Plan eine Gruppe, und auch das
            Datum des aktuellen Index lässt sich dort nachträglich korrigieren.
            Alles davon ändert <strong> keine Links</strong>.
          </>
        )}
      </div>

      {fehler && (
        <div className="project-website__error" onClick={() => setFehler(null)}>
          {fehler}
        </div>
      )}

      <div className="project-website__table-wrap">
        <table className="project-website__table planstand__table">
          <thead>
            <tr>
              <th className="planstand__col-griff" aria-label="Reihenfolge" />
              <th>Nummer</th>
              <th>Titel</th>
              <th>Quelle</th>
              <th>Index</th>
              <th>Datum</th>
              <th>PDF</th>
              <th>Link (aktueller Stand)</th>
              <th aria-label="Aktionen" />
            </tr>
          </thead>
          <tbody>
            {plaene.length === 0 && (
              <tr>
                <td className="project-website__empty" colSpan={SPALTEN}>
                  Noch keine Pläne erfasst.
                </td>
              </tr>
            )}

            {gruppen.map((gruppe) => (
              <React.Fragment key={gruppe.key || "__ohne_gruppe"}>
                {/* Solange keine einzige Gruppe vergeben ist, bleibt die Tabelle
                    flach — eine Kopfzeile „Ohne Gruppe" über allem wäre nur Lärm. */}
                {hatGruppen && (
                  <tr
                    className="planstand__gruppe-row"
                    onDragOver={(e) => {
                      if (!ziehtId) return;
                      e.preventDefault();
                      const ersterId = gruppe.plaene[0]?.id;
                      if (ersterId && ersterId !== ziehtId) {
                        setZiel({ id: ersterId, position: "vor" });
                      }
                    }}
                    onDrop={(e) => aufZeileAblegen(e, gruppe.plaene[0]?.id, gruppe.key)}
                  >
                    <td colSpan={SPALTEN}>
                      <div className="planstand__gruppe">
                        <span className="planstand__gruppe-titel">{gruppe.titel}</span>
                        <span className="planstand__gruppe-zahl">
                          {gruppe.plaene.length}{" "}
                          {gruppe.plaene.length === 1 ? "Plan" : "Pläne"}
                        </span>
                        {!disabled && gruppe.key !== "" && (
                          <span className="planstand__gruppe-aktionen">
                            <button
                              type="button"
                              disabled={busy}
                              title="Gruppe nach oben"
                              aria-label={`Gruppe ${gruppe.key} nach oben`}
                              onClick={() => gruppeVerschieben(gruppe.key, -1)}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              title="Gruppe nach unten"
                              aria-label={`Gruppe ${gruppe.key} nach unten`}
                              onClick={() => gruppeVerschieben(gruppe.key, 1)}
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => {
                                setUmbenennenFor(
                                  umbenennenFor === gruppe.key ? null : gruppe.key
                                );
                                setGruppenName(gruppe.key);
                              }}
                            >
                              Umbenennen
                            </button>
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                {umbenennenFor === gruppe.key && gruppe.key !== "" && (
                  <tr className="planstand__form-row">
                    <td colSpan={SPALTEN}>
                      <form onSubmit={(e) => gruppeUmbenennen(e, gruppe.key)}>
                        <span className="planstand__form-label">
                          Gruppe „{gruppe.key}" umbenennen ({gruppe.plaene.length}{" "}
                          {gruppe.plaene.length === 1 ? "Plan" : "Pläne"}):
                        </span>
                        <input
                          type="text"
                          value={gruppenName}
                          autoFocus
                          list="planstand-gruppen"
                          onChange={(e) => setGruppenName(e.target.value)}
                        />
                        <span className="planstand__form-hint">
                          Ein bereits vergebener Name führt die beiden Gruppen
                          zusammen.
                        </span>
                        <button type="submit" className="btn btn-primary" disabled={busy}>
                          Speichern
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setUmbenennenFor(null)}
                        >
                          Abbrechen
                        </button>
                      </form>
                    </td>
                  </tr>
                )}

                {gruppe.plaene.map((plan, position) => {
                  const aktuell =
                    plan.versionen.find((v) => !v.veraltet) || plan.versionen[0] || null;
                  const alte = plan.versionen.filter((v) => v.veraltet);
                  const historieOffen = historieFor === plan.id;
                  // Der aktive Stand ist nicht der jüngste Eintrag: entweder wurde ein
                  // Schreibvorgang abgebrochen (dann gilt der NEUE Index fälschlich als
                  // veraltet) oder jemand hat bewusst zurückgestellt. Nicht hinter dem
                  // Verlauf verstecken — es entscheidet, welcher Plan auf der Baustelle
                  // als gültig gilt.
                  const neuester =
                    plan.neuesteVersionId &&
                    plan.neuesteVersionId !== plan.aktuelleVersionId
                      ? plan.versionen.find((v) => v.id === plan.neuesteVersionId)
                      : null;

                  return (
                    <React.Fragment key={plan.id}>
                      <tr
                        // Ziehbar nur, wenn der Griff gepackt wurde: sonst würde jeder
                        // Versuch, Text in der Zeile zu markieren, ein Verschieben
                        // auslösen — und der Link ist genau zum Markieren da.
                        draggable={!disabled && griffFor === plan.id}
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = "move";
                          // Firefox startet ohne gesetzte Daten keinen Zug.
                          e.dataTransfer.setData("text/plain", plan.id);
                          setZiehtId(plan.id);
                        }}
                        onDragEnd={abwurfBeenden}
                        onDragOver={(e) => ueberZeile(e, plan.id)}
                        onDrop={(e) => aufZeileAblegen(e, plan.id, gruppe.key)}
                        className={
                          [
                            ziehtId === plan.id ? "is-zieht" : "",
                            zielIst(plan.id, "vor") ? "is-ziel-vor" : "",
                            zielIst(plan.id, "nach") ? "is-ziel-nach" : "",
                          ]
                            .filter(Boolean)
                            .join(" ") || undefined
                        }
                      >
                        <td className="planstand__cell-griff">
                          {/* Der Flex-Kasten sitzt INNEN. `display: flex` direkt auf
                              dem <td> nähme die Zelle aus dem Tabellenlayout heraus —
                              sie wüchse dann nicht mehr auf Zeilenhöhe und ihre
                              Unterkante läge neben der der übrigen Spalten. */}
                          {!disabled && (
                            <div className="planstand__griff-box">
                              <span
                                className="planstand__griff"
                                role="presentation"
                                title="Zum Verschieben ziehen"
                                onMouseDown={() => setGriffFor(plan.id)}
                                onMouseUp={() => setGriffFor(null)}
                              />
                              <span className="planstand__pfeile">
                                <button
                                  type="button"
                                  disabled={busy || position === 0}
                                  title="Eine Position nach oben (innerhalb der Gruppe)"
                                  aria-label={`${plan.nummer} nach oben`}
                                  onClick={() => planVerschieben(gruppe, position, -1)}
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  disabled={busy || position === gruppe.plaene.length - 1}
                                  title="Eine Position nach unten (innerhalb der Gruppe)"
                                  aria-label={`${plan.nummer} nach unten`}
                                  onClick={() => planVerschieben(gruppe, position, 1)}
                                >
                                  ↓
                                </button>
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="planstand__cell-nummer">{plan.nummer}</td>
                        <td>{plan.titel}</td>
                        <td className="planstand__cell-quelle">{plan.quelle || "–"}</td>
                        <td className="planstand__cell-index">{aktuell?.index || "–"}</td>
                        <td>{formatDate(aktuell?.datum) || "–"}</td>
                        <td className="planstand__cell-pdf">
                          <button type="button" className="planstand__pdf-placeholder" disabled>
                            PDF folgt
                          </button>
                        </td>
                        <td>
                          {aktuell ? (
                            <LinkZelle
                              url={buildPlanstandUrl(projectCode, aktuell.linkId)}
                              onQr={() => qrOeffnen(plan, aktuell)}
                            />
                          ) : (
                            "–"
                          )}
                        </td>
                        <td className="planstand__cell-actions">
                          <div className="planstand__aktionen">
                            <button
                              type="button"
                              disabled={disabled || busy}
                              onClick={() => {
                                setIndexFor(indexFor === plan.id ? null : plan.id);
                                setIndexForm({ index: "", datum: "" });
                                setMetaFor(null);
                              }}
                            >
                              Index aktualisieren
                            </button>
                            <button
                              type="button"
                              disabled={disabled || busy}
                              onClick={() => {
                                setMetaFor(metaFor === plan.id ? null : plan.id);
                                setMetaForm({
                                  nummer: plan.nummer,
                                  titel: plan.titel,
                                  quelle: plan.quelle,
                                  gruppe: plan.gruppe,
                                  datum: aktuell?.datum || "",
                                  versionId: aktuell?.id || "",
                                });
                                setIndexFor(null);
                              }}
                            >
                              Bearbeiten
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => setHistorieFor(historieOffen ? null : plan.id)}
                            >
                              {historieOffen
                                ? "Verlauf schliessen"
                                : `Verlauf (${plan.versionen.length})`}
                            </button>
                            <button
                              type="button"
                              className="planstand__delete"
                              disabled={disabled || busy}
                              onClick={() => planLoeschen(plan)}
                            >
                              Löschen
                            </button>
                          </div>
                        </td>
                      </tr>

                      {neuester && (
                        <tr className="planstand__warn-row">
                          <td colSpan={SPALTEN}>
                            <span className="planstand__warn-label">Prüfen</span>
                            Der zuletzt erfasste Stand (<strong>Index {neuester.index || "–"}</strong>
                            {neuester.datum ? `, ${formatDate(neuester.datum)}` : ""}) ist
                            <strong> nicht</strong> der aktive — aktiv ist Index{" "}
                            <strong>{aktuell?.index || "–"}</strong>. Extern gilt damit Index{" "}
                            {aktuell?.index || "–"} als gültiger Plan. Wurde ein Speichervorgang
                            abgebrochen, hier den richtigen Stand aktivieren.
                            {!disabled && (
                              <button
                                type="button"
                                className="planstand__warn-action"
                                disabled={busy}
                                onClick={() => standAktivieren(plan, neuester)}
                              >
                                Index {neuester.index || "–"} aktivieren
                              </button>
                            )}
                          </td>
                        </tr>
                      )}

                      {indexFor === plan.id && (
                        <tr className="planstand__form-row">
                          <td colSpan={SPALTEN}>
                            <form onSubmit={(e) => indexErfassen(e, plan.id)}>
                              <span className="planstand__form-label">
                                Neuer Index für {plan.nummer} {plan.titel}
                                {aktuell?.index ? ` (bisher ${aktuell.index})` : ""}:
                              </span>
                              <input
                                type="text"
                                placeholder="Index, z.B. B"
                                value={indexForm.index}
                                autoFocus
                                onChange={(e) =>
                                  setIndexForm((p) => ({ ...p, index: e.target.value }))
                                }
                              />
                              <input
                                type="date"
                                aria-label="Datum (optional)"
                                value={indexForm.datum}
                                onChange={(e) =>
                                  setIndexForm((p) => ({ ...p, datum: e.target.value }))
                                }
                              />
                              <span className="planstand__form-hint">Datum optional</span>
                              <button type="submit" className="btn btn-primary" disabled={busy}>
                                {busy ? "Speichern…" : "Neuen Link erzeugen"}
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setIndexFor(null)}
                              >
                                Abbrechen
                              </button>
                            </form>
                          </td>
                        </tr>
                      )}

                      {metaFor === plan.id && (
                        <tr className="planstand__form-row">
                          <td colSpan={SPALTEN}>
                            <form onSubmit={(e) => metaSpeichern(e, plan.id)}>
                              <span className="planstand__form-label">
                                Nummer, Titel, Quelle, Gruppe und das Datum des
                                aktuellen Index korrigieren (Links bleiben
                                unverändert):
                              </span>
                              <input
                                type="text"
                                placeholder="Nummer"
                                value={metaForm.nummer}
                                onChange={(e) =>
                                  setMetaForm((p) => ({ ...p, nummer: e.target.value }))
                                }
                              />
                              <input
                                type="text"
                                placeholder="Titel"
                                value={metaForm.titel}
                                onChange={(e) =>
                                  setMetaForm((p) => ({ ...p, titel: e.target.value }))
                                }
                              />
                              <input
                                type="text"
                                placeholder="Quelle, z.B. Gruner"
                                value={metaForm.quelle}
                                onChange={(e) =>
                                  setMetaForm((p) => ({ ...p, quelle: e.target.value }))
                                }
                              />
                              <input
                                type="text"
                                placeholder="Gruppe (leer = ohne)"
                                list="planstand-gruppen"
                                value={metaForm.gruppe}
                                onChange={(e) =>
                                  setMetaForm((p) => ({ ...p, gruppe: e.target.value }))
                                }
                              />
                              {metaForm.versionId && (
                                <input
                                  type="date"
                                  aria-label={`Datum des aktuellen Index (${aktuell?.index || "–"})`}
                                  title="Datum des aktuellen Index — rein kosmetisch, ändert weder Link noch QR-Code"
                                  value={metaForm.datum}
                                  onChange={(e) =>
                                    setMetaForm((p) => ({ ...p, datum: e.target.value }))
                                  }
                                />
                              )}
                              <span className="planstand__form-hint">
                                Das Datum betrifft nur Index {aktuell?.index || "–"} —
                                Link und QR-Code bleiben gleich.
                              </span>
                              <button type="submit" className="btn btn-primary" disabled={busy}>
                                Speichern
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setMetaFor(null)}
                              >
                                Abbrechen
                              </button>
                            </form>
                          </td>
                        </tr>
                      )}

                      {historieOffen && (
                        <tr className="planstand__historie-row">
                          <td colSpan={SPALTEN}>
                            <ul className="planstand__historie">
                              {plan.versionen.map((v) => (
                                <li
                                  key={v.id}
                                  className={
                                    v.veraltet ? "planstand__historie-item is-veraltet" : "planstand__historie-item"
                                  }
                                >
                                  <span className="planstand__historie-index">
                                    Index {v.index || "–"}
                                  </span>
                                  <span className="planstand__historie-datum">
                                    {formatDate(v.datum) || "ohne Datum"}
                                  </span>
                                  <span className="planstand__historie-status">
                                    {v.veraltet ? "veraltet" : "aktueller Stand"}
                                  </span>
                                  <LinkZelle
                                    url={buildPlanstandUrl(projectCode, v.linkId)}
                                    veraltet={v.veraltet}
                                    onQr={() => qrOeffnen(plan, v)}
                                  />
                                  {v.veraltet && !disabled && (
                                    <button
                                      type="button"
                                      className="planstand__historie-action"
                                      disabled={busy}
                                      onClick={() => standAktivieren(plan, v)}
                                    >
                                      als aktuell setzen
                                    </button>
                                  )}
                                </li>
                              ))}
                            </ul>
                            {alte.length > 0 && (
                              <p className="planstand__historie-hint">
                                Veraltete Links bleiben erreichbar und zeigen extern den
                                Hinweis, dass mit Index {aktuell?.index} ein neuerer Stand
                                vorliegt.
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {!disabled && !neuOffen && (
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setNeuOffen(true)}
          disabled={busy}
        >
          Plan hinzufügen
        </button>
      )}

      {!disabled && neuOffen && (
        <form className="planstand__neu" onSubmit={planAnlegen}>
          <input
            type="text"
            placeholder="Nummer, z.B. GR-101"
            value={neuForm.nummer}
            autoFocus
            onChange={(e) => setNeuForm((p) => ({ ...p, nummer: e.target.value }))}
          />
          <input
            type="text"
            placeholder="Titel, z.B. Grundriss EG"
            value={neuForm.titel}
            onChange={(e) => setNeuForm((p) => ({ ...p, titel: e.target.value }))}
          />
          <input
            type="text"
            placeholder="Quelle, z.B. Gruner (optional)"
            value={neuForm.quelle}
            onChange={(e) => setNeuForm((p) => ({ ...p, quelle: e.target.value }))}
          />
          <input
            type="text"
            placeholder="Gruppe (optional)"
            list="planstand-gruppen"
            value={neuForm.gruppe}
            onChange={(e) => setNeuForm((p) => ({ ...p, gruppe: e.target.value }))}
          />
          <input
            type="text"
            placeholder="Index, z.B. A"
            value={neuForm.index}
            onChange={(e) => setNeuForm((p) => ({ ...p, index: e.target.value }))}
          />
          <input
            type="date"
            aria-label="Datum (optional)"
            value={neuForm.datum}
            onChange={(e) => setNeuForm((p) => ({ ...p, datum: e.target.value }))}
          />
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Speichern…" : "Plan erfassen"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setNeuOffen(false);
              setNeuForm(emptyPlanForm());
              setFehler(null);
            }}
          >
            Abbrechen
          </button>
        </form>
      )}

      {/* Vorschläge für alle Gruppenfelder — eine Gruppe soll nicht durch einen
          Tippfehler versehentlich zur zweiten werden. */}
      <datalist id="planstand-gruppen">
        {gruppenNamen.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>

      {qrFor && <PlanstandQrDialog {...qrFor} onClose={() => setQrFor(null)} />}
    </div>
  );
};

export default ProjectWebsitePlanstand;
